import { pgTable, unique, date, uuid, foreignKey, integer, boolean, uniqueIndex, index, text, } from 'drizzle-orm/pg-core';
import { orgTableBase, orgPrimaryKey } from '../org-id';
import { Block } from '.';
import { Profile } from '../profile';
import { RecordType, WorkoutStatus } from '../../enums';
import { relations, sql } from 'drizzle-orm';
import { defaultLayer } from '../../json/layer';
import { isoTimestamp } from '../../utils';
import { configColumn } from '../../json';
export const BlockDraftWorkoutLayer = pgTable('block_draft_workout_layer', {
    ...orgTableBase,
    blockId: uuid('block_id').notNull(),
    cycleSortOrder: integer('cycle_sort_order').notNull(),
    sessionSortOrder: integer('session_sort_order').notNull(),
    workoutId: uuid('workout_id').notNull(),
    assignedDate: date('assigned_date', {
        mode: 'string',
    }),
    unpublishedChangeCount: integer('unpublished_change_count').notNull(),
    layer: configColumn('layer', defaultLayer).notNull(),
    deletedAt: isoTimestamp('deleted_at'),
    name: text('name'),
}, (table) => ({
    pk: orgPrimaryKey(table),
    // Partial unique index: only non-deleted rows hold the slot key, so a
    // soft-deleted draft day cannot block re-creating the same slot
    // (org_id, block_id, workout_id) — see SC-4902.
    activeSlotUqIdx: uniqueIndex('active_block_draft_workout_layer_uq_idx')
        .on(table.orgId, table.blockId, table.workoutId)
        .where(sql `${table.deletedAt} IS NULL`),
    blockFk: foreignKey({
        columns: [table.orgId, table.blockId],
        foreignColumns: [Block.orgId, Block.id],
    }),
}));
export const AthleteDraftWorkoutLayer = pgTable('athlete_draft_workout_layer', {
    ...orgTableBase,
    profileId: uuid('profile_id').notNull(),
    blockId: uuid('block_id').notNull(),
    cycleSortOrder: integer('cycle_sort_order').notNull(),
    sessionSortOrder: integer('session_sort_order').notNull(),
    workoutId: uuid('workout_id').notNull(),
    assignedDate: date('assigned_date', {
        mode: 'string',
    }),
    excludeAssignedDate: boolean('exclude_assigned_date')
        .notNull()
        .default(false),
    unpublishedChangeCount: integer('unpublished_change_count').notNull(),
    layer: configColumn('layer', defaultLayer).notNull(),
    deletedAt: isoTimestamp('deleted_at'),
    name: text('name'),
}, (table) => ({
    pk: orgPrimaryKey(table),
    uq: unique().on(table.orgId, table.blockId, table.workoutId, table.profileId),
    athleteFk: foreignKey({
        columns: [table.orgId, table.profileId],
        foreignColumns: [Profile.orgId, Profile.id],
    }),
    blockFk: foreignKey({
        columns: [table.orgId, table.blockId],
        foreignColumns: [Block.orgId, Block.id],
    }),
}));
export const BlockPublishedWorkoutLayer = pgTable('block_published_workout_layer', {
    ...orgTableBase,
    blockId: uuid('block_id').notNull(),
    cycleSortOrder: integer('cycle_sort_order').notNull(),
    sessionSortOrder: integer('session_sort_order').notNull(),
    workoutId: uuid('workout_id').notNull(),
    version: integer('version').notNull(),
    isCurrent: boolean('is_current').notNull().default(false),
    status: WorkoutStatus('status').notNull().default('planned'),
    assignedDate: date('assigned_date', {
        mode: 'string',
    }),
    layer: configColumn('layer', defaultLayer).notNull(),
    name: text('name'),
}, (table) => ({
    pk: orgPrimaryKey(table),
    uq: unique().on(table.orgId, table.blockId, table.workoutId, table.version),
    currentUqIdx: uniqueIndex('current_block_uq_idx')
        .on(table.orgId, table.blockId, table.workoutId)
        .where(sql `${table.isCurrent} IS TRUE`),
}));
export const Block_Published_Workout_Layer_Relations = relations(BlockPublishedWorkoutLayer, ({ one }) => ({
    block: one(Block, {
        fields: [
            BlockPublishedWorkoutLayer.orgId,
            BlockPublishedWorkoutLayer.blockId,
        ],
        references: [Block.orgId, Block.id],
    }),
}));
export const AthletePublishedWorkoutLayer = pgTable('athlete_published_workout_layer', {
    ...orgTableBase,
    profileId: uuid('profile_id').notNull(),
    blockId: uuid('block_id').notNull(),
    cycleSortOrder: integer('cycle_sort_order').notNull(),
    sessionSortOrder: integer('session_sort_order').notNull(),
    workoutId: uuid('workout_id').notNull(),
    version: integer('version').notNull(),
    blockVersion: integer('block_version').notNull(),
    isCurrent: boolean('is_current').notNull().default(false),
    status: WorkoutStatus('status').notNull().default('planned'),
    statusUpdatedAt: isoTimestamp('status_updated_at'),
    assignedDate: date('assigned_date', {
        mode: 'string',
    }),
    layer: configColumn('layer', defaultLayer).notNull(),
    name: text('name'),
    // Nullable; null means a normal, user-created workout layer. Non-null values
    // mark rows created programmatically — see RecordTypes in enums.ts.
    recordType: RecordType('record_type'),
}, (table) => ({
    pk: orgPrimaryKey(table),
    uq: unique().on(table.orgId, table.blockId, table.workoutId, table.profileId, table.version),
    currentUqIdx: uniqueIndex('current_athlete_block_uq_idx')
        .on(table.orgId, table.blockId, table.workoutId, table.profileId)
        .where(sql `${table.isCurrent} IS TRUE`),
    // Tablet athlete bootstrap filters this table by (orgId, profileId); the
    // other indexes all lead with blockId (SC-5522).
    profileIdx: index('idx_athlete_published_workout_layer_org_profile').on(table.orgId, table.profileId),
    athleteFk: foreignKey({
        columns: [table.orgId, table.profileId],
        foreignColumns: [Profile.orgId, Profile.id],
    }),
    blockFk: foreignKey({
        columns: [table.orgId, table.blockId],
        foreignColumns: [Block.orgId, Block.id],
    }),
}));
export const AthleteActualWorkoutLayer = pgTable('athlete_actual_workout_layer', {
    ...orgTableBase,
    profileId: uuid('profile_id').notNull(),
    blockId: uuid('block_id').notNull(),
    workoutId: uuid('workout_id').notNull(),
    athleteVersion: integer('athlete_version').notNull(),
    blockVersion: integer('block_version').notNull(),
    layer: configColumn('layer', defaultLayer).notNull(),
}, (table) => ({
    pk: orgPrimaryKey(table),
    uq: unique().on(
    // We expect one actual layer per block/workout/layer for an athlete
    table.orgId, table.blockId, table.workoutId, table.profileId),
    // Tablet athlete bootstrap filters this table by (orgId, profileId); the
    // unique constraint leads with blockId (SC-5522).
    profileIdx: index('idx_athlete_actual_workout_layer_org_profile').on(table.orgId, table.profileId),
    athleteFk: foreignKey({
        columns: [table.orgId, table.profileId],
        foreignColumns: [Profile.orgId, Profile.id],
    }),
    athleteBlockFk: foreignKey({
        columns: [
            table.orgId,
            table.blockId,
            table.workoutId,
            table.profileId,
            table.athleteVersion,
        ],
        foreignColumns: [
            AthletePublishedWorkoutLayer.orgId,
            AthletePublishedWorkoutLayer.blockId,
            AthletePublishedWorkoutLayer.workoutId,
            AthletePublishedWorkoutLayer.profileId,
            AthletePublishedWorkoutLayer.version,
        ],
    }),
}));
export const AthleteModifiedPublishedWorkoutLayer = pgTable('athlete_modified_published_workout_layer', {
    ...orgTableBase,
    profileId: uuid('profile_id').notNull(),
    blockId: uuid('block_id').notNull(),
    workoutId: uuid('workout_id').notNull(),
    athleteVersion: integer('athlete_version').notNull(),
    blockVersion: integer('block_version').notNull(),
    layer: configColumn('layer', defaultLayer).notNull(),
}, (table) => ({
    pk: orgPrimaryKey(table),
    uq: unique().on(table.orgId, table.blockId, table.workoutId, table.profileId),
    // Tablet athlete bootstrap filters this table by (orgId, profileId); the
    // unique constraint leads with blockId (SC-5522).
    profileIdx: index('idx_athlete_modified_published_workout_layer_org_profile').on(table.orgId, table.profileId),
    athleteFk: foreignKey({
        columns: [table.orgId, table.profileId],
        foreignColumns: [Profile.orgId, Profile.id],
    }),
    athleteBlockFk: foreignKey({
        columns: [
            table.orgId,
            table.blockId,
            table.workoutId,
            table.profileId,
            table.athleteVersion,
        ],
        foreignColumns: [
            AthletePublishedWorkoutLayer.orgId,
            AthletePublishedWorkoutLayer.blockId,
            AthletePublishedWorkoutLayer.workoutId,
            AthletePublishedWorkoutLayer.profileId,
            AthletePublishedWorkoutLayer.version,
        ],
    }),
}));
export const Athlete_Published_Workout_Layer_Relations = relations(AthletePublishedWorkoutLayer, ({ one }) => ({
    block: one(Block, {
        fields: [
            AthletePublishedWorkoutLayer.orgId,
            AthletePublishedWorkoutLayer.blockId,
        ],
        references: [Block.orgId, Block.id],
    }),
    blockPublishedWorkoutLayer: one(BlockPublishedWorkoutLayer, {
        fields: [
            AthletePublishedWorkoutLayer.orgId,
            AthletePublishedWorkoutLayer.blockId,
            AthletePublishedWorkoutLayer.workoutId,
            AthletePublishedWorkoutLayer.blockVersion,
        ],
        references: [
            BlockPublishedWorkoutLayer.orgId,
            BlockPublishedWorkoutLayer.blockId,
            BlockPublishedWorkoutLayer.workoutId,
            BlockPublishedWorkoutLayer.version,
        ],
    }),
    athleteActualWorkoutLayer: one(AthleteActualWorkoutLayer, {
        fields: [
            AthletePublishedWorkoutLayer.orgId,
            AthletePublishedWorkoutLayer.blockId,
            AthletePublishedWorkoutLayer.workoutId,
            AthletePublishedWorkoutLayer.profileId,
            AthletePublishedWorkoutLayer.blockVersion,
            AthletePublishedWorkoutLayer.version,
        ],
        references: [
            AthleteActualWorkoutLayer.orgId,
            AthleteActualWorkoutLayer.blockId,
            AthleteActualWorkoutLayer.workoutId,
            AthleteActualWorkoutLayer.profileId,
            AthleteActualWorkoutLayer.blockVersion,
            AthleteActualWorkoutLayer.athleteVersion,
        ],
    }),
    athleteModifiedPublishedWorkoutLayer: one(AthleteModifiedPublishedWorkoutLayer, {
        fields: [
            AthletePublishedWorkoutLayer.orgId,
            AthletePublishedWorkoutLayer.blockId,
            AthletePublishedWorkoutLayer.workoutId,
            AthletePublishedWorkoutLayer.profileId,
            AthletePublishedWorkoutLayer.version,
        ],
        references: [
            AthleteModifiedPublishedWorkoutLayer.orgId,
            AthleteModifiedPublishedWorkoutLayer.blockId,
            AthleteModifiedPublishedWorkoutLayer.workoutId,
            AthleteModifiedPublishedWorkoutLayer.profileId,
            AthleteModifiedPublishedWorkoutLayer.athleteVersion,
        ],
    }),
}));
//# sourceMappingURL=workout.js.map